import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getDjangoEndpoint } from "src/utils/apiEndpoints";
import axiosInstance from "src/store/features/auth/axiosCSRF";
import { getApiErrorMessage } from "src/types/api";
export interface iWorkFlowRunState {
  response: any;
  isLoading: boolean;
  message: string | null;
}

const initialState: iWorkFlowRunState = {
  response: null,
  isLoading: false,
  message: "",
};

// 공통 필수 파라미터
interface BaseWorkflowParams {
  workflow_name: string;
  group_name: string;
  library_name: string;
  round: number;
  num_mol?: number;
  timestamp?: string;
  re_run?: boolean;
  sample_use?: boolean;
}

// PPI Inhibitor 전용 파라미터
interface PPIInhibitorParams {
  drug_file_name: string;
  drug_file_ext: string;
  target_file_name: string;
  target_file_ext: string;
  assay_file_name: string;
  assay_file_ext: string;
  protein_name?: string;
  protein_seq?: string;
  chain?: string;
  ligand_id?: string;
  filter_plip?: Record<string, any>;
  filter_adme_threshold?: number;
  filter_druglikeness?: Record<string, any>;
  coordinates?: any[];
  assay_id?: string;
  gt_label?: string;
  assay_smiles_col?: string;
  residues : string[];
}

// Kinase Inhibitor 전용 파라미터
interface KinaseInhibitorParams {
  kinase_target: string;
  kinase_off_targets: string;
  assay_file_name: string;
  assay_file_ext: string;
  filter_druglikeness?: Record<string, any>;
  filter_plip?: Record<string, any>;
  filter_kinase?: Record<string, any>;
  filter_adme_threshold?: number;
  assay_info?: Record<string, any>;
  kinase_info?: Record<string, any>;
  min_hba_hbd_sum?: number;
  qeppi_threshold?: number;
  sa_threshold?: number;
}

interface DrugResponseParams {
  smiles: string;
  cell_lines: string[];
  gene_expression_file_name : string;
  gene_expression_file_ext : string;
  drug_response_tool: string[];
}

interface DrugToxicityParams {
  smiles: string;
  compound_name?: string;
}

interface MdtrParams {
  workflow_type: string;
  expression_file_name: string;
  expression_file_ext: string;
  input_type: string;
  control: string;
}

interface BiomarkerParams {
  h5ad_file_name: string;
  h5ad_file_ext: string;
  patient_col: string;
  celltype_col: string;
  label_col: string;
  positive_labels: string;
  negative_labels: string;
}

// 모든 워크플로우 파라미터를 포함하는 유니온 타입
export type WorkflowRunParams = BaseWorkflowParams &
  Partial<PPIInhibitorParams> &
  Partial<KinaseInhibitorParams> &
  Partial<DrugResponseParams> &
  Partial<DrugToxicityParams> &
  Partial<MdtrParams> &
  Partial<BiomarkerParams>;

export const workFlowRunAPI = createAsyncThunk(
  "workFlowRunAPI",
  async (data: WorkflowRunParams, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const pathParam = '/workflow/run/';

      // 기본 공통 필드
      const requestData: Record<string, unknown> = {
        workflow_name: data.workflow_name,
        group_name: data.group_name,
        library_name: data.library_name,
        round: data.round,
      };

      // null, undefined, 빈 문자열이 아닌 값들만 추가하는 헬퍼 함수
      const addIfValid = (key: string, value: unknown) => {
        if (value !== null && value !== undefined && value !== "") {
          requestData[key] = value;
        }
      };

      // 배열인 경우에만 추가하는 헬퍼 함수
      const addIfArray = (key: string, value: unknown) => {
        if (value !== undefined && value !== null && Array.isArray(value) && value.length > 0) {
          requestData[key] = value;
        }
      };

      // PPI Inhibitor 관련 필드
      addIfValid('drug_file_name', data.drug_file_name);
      addIfValid('drug_file_ext', data.drug_file_ext);
      addIfValid('target_file_name', data.target_file_name);
      addIfValid('target_file_ext', data.target_file_ext);
      addIfValid('protein_name', data.protein_name);
      addIfValid('protein_seq', data.protein_seq);
      addIfValid('residues', data.residues);
      addIfValid('chain', data.chain);
      addIfValid('ligand_id', data.ligand_id);
      addIfValid('assay_id', data.assay_id);
      addIfValid('gt_label', data.gt_label);
      addIfValid('assay_smiles_col', data.assay_smiles_col);
      addIfArray('coordinates', data.coordinates);

      // Kinase Inhibitor 관련 필드
      addIfValid('kinase_target', data.kinase_target);
      addIfValid('kinase_off_targets', data.kinase_off_targets);

      // 공통 필드
      addIfValid('assay_file_name', data.assay_file_name);
      addIfValid('assay_file_ext', data.assay_file_ext);
      addIfValid('num_mol', data.num_mol);
      addIfValid('smiles', data.smiles);
      addIfValid('compound_name', data.compound_name);
      addIfValid('workflow_type', data.workflow_type);
      addIfValid('expression_file_name', data.expression_file_name);
      addIfValid('expression_file_ext', data.expression_file_ext);
      addIfValid('input_type', data.input_type);
      addIfValid('control', data.control);
      addIfArray('cell_lines', data.cell_lines);
      addIfValid('gene_expression_file_name', data.gene_expression_file_name);
      addIfValid('gene_expression_file_ext', data.gene_expression_file_ext);
      addIfArray('drug_response_tool', data.drug_response_tool);
      addIfValid('h5ad_file_name', data.h5ad_file_name);
      addIfValid('h5ad_file_ext', data.h5ad_file_ext);
      addIfValid('patient_col', data.patient_col);
      addIfValid('celltype_col', data.celltype_col);
      addIfValid('label_col', data.label_col);
      addIfValid('positive_labels', data.positive_labels);
      addIfValid('negative_labels', data.negative_labels);

      // 객체 타입 필터들 (null/undefined 체크만)
      if (data.filter_plip !== null && data.filter_plip !== undefined) {
        requestData.filter_plip = data.filter_plip;
      }
      
      if (data.filter_druglikeness !== null && data.filter_druglikeness !== undefined) {
        requestData.filter_druglikeness = data.filter_druglikeness;
      }
      
      if (data.filter_kinase !== null && data.filter_kinase !== undefined) {
        requestData.filter_kinase = data.filter_kinase;
      }
      
      if (data.assay_info !== null && data.assay_info !== undefined) {
        requestData.assay_info = data.assay_info;
      }
      
      if (data.kinase_info !== null && data.kinase_info !== undefined) {
        requestData.kinase_info = data.kinase_info;
      }
      if (data.residues !== null && data.residues !== undefined) {
        requestData.residues = data.residues;
      }

      // Kinase workflow는 kinase 필터 키를 항상 포함해야 함
      if (data.workflow_name === "Kinase Inhibitor") {
        if (requestData.filter_kinase === undefined || requestData.filter_kinase === null) {
          requestData.filter_kinase = {};
        }
      }

      // PPI/Kinase workflow는 PLIP 필터 키를 항상 포함해야 함
      if (data.workflow_name === "Kinase Inhibitor" || data.workflow_name === "PPI Inhibitor") {
        if (requestData.filter_plip === undefined || requestData.filter_plip === null) {
          requestData.filter_plip = { plip: {} };
        }
      }

      // 숫자 타입 필드들 (0도 유효한 값)
      if (data.filter_adme_threshold !== null && data.filter_adme_threshold !== undefined) {
        requestData.filter_adme_threshold = data.filter_adme_threshold;
      }
      
      if (data.min_hba_hbd_sum !== null && data.min_hba_hbd_sum !== undefined) {
        requestData.min_hba_hbd_sum = data.min_hba_hbd_sum;
      }
      
      if (data.qeppi_threshold !== null && data.qeppi_threshold !== undefined) {
        requestData.qeppi_threshold = data.qeppi_threshold;
      }
      
      if (data.sa_threshold !== null && data.sa_threshold !== undefined) {
        requestData.sa_threshold = data.sa_threshold;
      }

      // 메타 필드들
      if (data.timestamp !== undefined) {
        requestData.timestamp = data.timestamp;
      }

      if (data.re_run !== undefined) {
        requestData.re_run = data.re_run;
      }
      if (data.sample_use !== undefined) {
        requestData.sample_use = data.sample_use;
      }

      const response = await axiosInstance.post(
        `${djangoEndpoint}${pathParam}`,
        requestData,
        { withCredentials: true }
      );

      // 응답에 추가 정보 포함
      const responseData = response.data;
      
      if (data.ligand_id !== undefined) {
        responseData.ligand_id = data.ligand_id;
      }

      if (data.timestamp !== undefined) {
        responseData.timestamp = data.timestamp;
      }

      if (data.smiles !== undefined) {
        responseData.smiles = data.smiles;
      }

      if (data.cell_lines !== undefined) {
        responseData.cell_lines = data.cell_lines;
      }

      if (data.gene_expression_file_name !== undefined){
        responseData.gene_expression_file_name = data.gene_expression_file_name
      }
      if (data.gene_expression_file_ext !== undefined){
        responseData.gene_expression_file_ext = data.gene_expression_file_ext
      }

      return responseData;
    } catch (error) {
      console.error("Error in workFlowRunAPI:", error);

      const errorMessage = getApiErrorMessage(error);

      return rejectWithValue(errorMessage);
    }
  }
);

export const workFlowRunSlice = createSlice({
  name: "workFlowRun",
  initialState,
  reducers: {
    // 필요시 응답 초기화 등의 액션 추가 가능
    resetWorkflowRun: (state) => {
      state.response = null;
      state.isLoading = false;
      state.message = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(workFlowRunAPI.pending, (state) => {
        state.isLoading = true;
        state.message = "";
      })
      .addCase(workFlowRunAPI.fulfilled, (state, action) => {
        state.isLoading = false;
        state.response = action.payload;
      })
      .addCase(workFlowRunAPI.rejected, (state, action) => {
        state.isLoading = false;
        state.message = action.payload as string;
      });
  },
});

export const { resetWorkflowRun } = workFlowRunSlice.actions;
export default workFlowRunSlice.reducer;
